import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/shared/components/ui/badge"
import { Button } from "@/shared/components/ui/button"
import { Mail, MailOpen, Trash2, Search, ChevronLeft, ChevronRight, MoreVertical, Archive, MessageSquare, Eye } from "lucide-react"
import { Input } from "@/shared/components/ui/input"
import { Checkbox } from "@/shared/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { useToast } from "@/shared/components/Toast"
import { AdminPageHeader, AdminSectionCard } from "@/features/admin/components/common/layout"

interface Message {
  id: number
  name: string
  email: string
  phone?: string
  subject: string
  best_time_to_call?: string
  message: string
  created_at: string
  status: 'new' | 'read' | 'replied' | 'archived'
}

export default function Messages() {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const { showToast } = useToast()

  useEffect(() => {
    fetchMessages()
  }, [statusFilter, currentPage, itemsPerPage])

  const fetchMessages = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: ((currentPage - 1) * itemsPerPage).toString(),
      })
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }

      const response = await fetch(`/api/contact-messages.php?${params}`)
      const data = await response.json()
      
      if (data.success) {
        setMessages(data.data || [])
        setTotalRecords(data.total || 0)
      } else {
        showToast({
          type: 'error',
          title: 'Error',
          message: data.message || 'Failed to load messages'
        })
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to load messages'
      })
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (id: number) => {
    try {
      const response = await fetch('/api/contact-messages.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: 'read' })
      })
      
      const data = await response.json()
      if (data.success) {
        setMessages(messages.map(msg => 
          msg.id === id ? { ...msg, status: 'read' } : msg
        ))
        showToast({
          type: 'success',
          title: 'Success',
          message: 'Message marked as read'
        })
      }
    } catch (error) {
      console.error('Error marking message as read:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to update message'
      })
    }
  }

  const deleteMessage = async (id: number) => {
    if (!confirm('Are you sure you want to delete this message?')) return
    
    try {
      const response = await fetch('/api/contact-messages.php', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      })
      
      const data = await response.json()
      if (data.success) {
        setMessages(messages.filter(msg => msg.id !== id))
        setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
        showToast({
          type: 'success',
          title: 'Success',
          message: 'Message deleted successfully'
        })
      }
    } catch (error) {
      console.error('Error deleting message:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete message'
      })
    }
  }

  const deleteSelectedMessages = async () => {
    if (selectedIds.length === 0) return
    if (!confirm(`Are you sure you want to delete ${selectedIds.length} selected message(s)?`)) return
    
    try {
      const deletePromises = selectedIds.map(id =>
        fetch('/api/contact-messages.php', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id })
        })
      )
      
      await Promise.all(deletePromises)
      setMessages(messages.filter(msg => !selectedIds.includes(msg.id)))
      setSelectedIds([])
      showToast({
        type: 'success',
        title: 'Success',
        message: `${selectedIds.length} message(s) deleted successfully`
      })
    } catch (error) {
      console.error('Error deleting messages:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete messages'
      })
    }
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredMessages.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(filteredMessages.map(msg => msg.id))
    }
  }

  const toggleSelectMessage = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id]
    )
  }

  const viewMessage = (message: Message) => {
    setSelectedMessage(message)
    setViewDialogOpen(true)
    if (message.status === 'new') {
      markAsRead(message.id)
    }
  }

  const filteredMessages = messages.filter(msg =>
    msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.message.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const unreadCount = messages.filter(msg => msg.status === 'new').length
  const totalPages = Math.ceil(totalRecords / itemsPerPage)

  const getStatusBadge = (status: string) => {
    const config = {
      new: { label: 'New', className: 'bg-blue-100 text-blue-800' },
      read: { label: 'Read', className: 'bg-gray-100 text-gray-800' },
      replied: { label: 'Replied', className: 'bg-green-100 text-green-800' },
      archived: { label: 'Archived', className: 'bg-purple-100 text-purple-800' },
    }
    const { label, className } = config[status as keyof typeof config] || config.new
    return <Badge className={className}>{label}</Badge>
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Messages"
        description="Contact form submissions from customers"
        icon={MessageSquare}
        badge={unreadCount > 0 ? { label: `${unreadCount} Unread`, variant: "default" } : undefined}
        actions={[
          {
            label: "View Deleted",
            icon: Archive,
            onClick: () => navigate('/admin/messages/deleted'),
            variant: "outline"
          }
        ]}
      />

      <AdminSectionCard>
        <div className="p-3 sm:p-6 pb-0 sm:pb-0">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="replied">Replied</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2 mt-3 sm:mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <span className="text-sm font-medium text-slate-700">{selectedIds.length} selected</span>
              <Button
                variant="destructive"
                size="sm"
                onClick={deleteSelectedMessages}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          )}
        </div>
        <div className="p-0 sm:p-6 sm:pt-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mb-3" />
              <p className="text-muted-foreground font-medium">Loading messages...</p>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted mb-4">
                <MessageSquare className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1">No messages found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <>
              <div className="sm:rounded-md border-y sm:border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={selectedIds.length === filteredMessages.length && filteredMessages.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMessages.map((message) => (
                      <TableRow 
                        key={message.id} 
                        className={message.status === 'new' ? 'bg-blue-50/50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(message.id)}
                            onCheckedChange={() => toggleSelectMessage(message.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {message.status === 'new' ? (
                            <Mail className="h-5 w-5 text-blue-600" />
                          ) : (
                            <MailOpen className="h-5 w-5 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{message.name}</TableCell>
                        <TableCell>{message.email}</TableCell>
                        <TableCell className="max-w-[300px] truncate">{message.subject}</TableCell>
                        <TableCell>{getStatusBadge(message.status)}</TableCell>
                        <TableCell>
                          {new Date(message.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => viewMessage(message)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Message
                              </DropdownMenuItem>
                              {message.status === 'new' && (
                                <DropdownMenuItem
                                  onClick={() => markAsRead(message.id)}
                                >
                                  <MailOpen className="h-4 w-4 mr-2" />
                                  Mark as Read
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => deleteMessage(message.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              <div className="flex flex-col gap-4 p-3 sm:p-4 border-t border-gray-100">
                {/* Results info - centered on mobile */}
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {totalRecords === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} messages
                </div>
                
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Rows per page selector */}
                  <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page:</span>
                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(value) => {
                        setItemsPerPage(Number(value))
                        setCurrentPage(1)
                      }}
                    >
                      <SelectTrigger className="w-16 h-9 sm:h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Pagination controls */}
                  <div className="flex items-center justify-center gap-1 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1 || totalPages === 0}
                      className="h-9 w-9 sm:h-8 sm:w-8 p-0 touch-manipulation"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <ChevronLeft className="h-4 w-4 -ml-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1 || totalPages === 0}
                      className="h-9 w-9 sm:h-8 sm:w-8 p-0 touch-manipulation"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-1 mx-2">
                      <span className="text-sm font-medium hidden sm:inline">Page</span>
                      <Input
                        type="number"
                        min={1}
                        max={totalPages || 1}
                        value={currentPage}
                        onChange={(e) => {
                          const page = Number(e.target.value)
                          if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page)
                          }
                        }}
                        className="w-12 h-9 sm:h-8 text-center p-1"
                        disabled={totalPages === 0}
                      />
                      <span className="text-sm text-muted-foreground">/ {totalPages || 1}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="h-9 w-9 sm:h-8 sm:w-8 p-0 touch-manipulation"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="h-9 w-9 sm:h-8 sm:w-8 p-0 touch-manipulation"
                    >
                      <ChevronRight className="h-4 w-4" />
                      <ChevronRight className="h-4 w-4 -ml-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </AdminSectionCard>

      {/* View Message Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl" aria-labelledby="view-message-title" aria-describedby="view-message-desc">
          <DialogHeader>
            <VisuallyHidden>
              <DialogTitle id="view-message-title">Message Details</DialogTitle>
            </VisuallyHidden>
            <DialogDescription id="view-message-desc">
              Contact form submission
            </DialogDescription>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Name</label>
                  <p className="text-sm">{selectedMessage.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-sm">{selectedMessage.email}</p>
                </div>
                {selectedMessage.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="text-sm">{selectedMessage.phone}</p>
                  </div>
                )}
                {selectedMessage.best_time_to_call && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Best Time to Call</label>
                    <p className="text-sm">{selectedMessage.best_time_to_call}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-500">Subject</label>
                  <p className="text-sm">{selectedMessage.subject}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-500">Message</label>
                  <p className="text-sm whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedMessage.status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Date Received</label>
                  <p className="text-sm">{new Date(selectedMessage.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
